import { useQuery } from '@apollo/client';
import { RootState, useTypedSelector } from 'reducers';
import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import {
  AccountType,
  KeywordSearchOperator,
  AnyObject,
} from '@xbcb/shared-types';
import { Login, SearchCustomsBrokersQuery } from 'libs/sharedQueries';
import { RumUIEvent } from '@xbcb/ui-types';
import { recordUIEvent } from '@xbcb/ui-utils';

import { isMonsEnv } from '@xbcb/ui-env';
import { isSSOEnabled } from 'featureFlags';
import { getUserAccountType } from 'libs/getUserAccountType';
import { isMigrateWorkflowPage } from '../../isMigrateWorkflowPage';
import { awsRumClient } from '@xbcb/cloudwatch-rum';
import { AuthenticationContextDocument, Tag } from '__generated__/graphql';

const isMons = isMonsEnv();

// TODO use autogenerated return type from apollo client when ready -> https://app.asana.com/0/1206674078292078/1209100648515887/f
export const useCurrentUser = () => {
  const [rumContextUpdated, setRumContextUpdated] = useState(false);
  const currentActiveBrokerId: string | undefined = useSelector(
    (state: RootState) => state.activeBroker.currentActiveBroker,
  );
  const { customType = '', userToken = '' } = useTypedSelector(
    ({ userLogin }: RootState) => ({
      userToken: userLogin.userToken,
      customType: userLogin['custom:type'],
    }),
  );

  const shouldUseAuthenticationContext = isMons && isSSOEnabled;

  const activated = !['onboarding', 'pending'].includes(customType);
  const variables = isMons ? {} : { cognitoSub: userToken };
  const isMigratePage = isMigrateWorkflowPage();
  const skipQueries =
    isMigratePage || (isMons ? false : !userToken || !activated);
  const { data, error, loading } = useQuery(
    shouldUseAuthenticationContext ? AuthenticationContextDocument : Login,
    {
      variables,
      skip: skipQueries,
    },
  );

  const { data: queryResponse } = useQuery(SearchCustomsBrokersQuery, {
    variables: {
      input: {
        searchCriteria: {
          id: {
            operator: KeywordSearchOperator.ONE_OF,
            values: [currentActiveBrokerId],
          },
        },
      },
    },
    skip: skipQueries || !currentActiveBrokerId,
  });

  const user = shouldUseAuthenticationContext
    ? data?.authenticationContext?.authenticatedUserContext?.user
    : data?.login?.user;

  const role = shouldUseAuthenticationContext
    ? data?.authenticationContext?.authenticatedUserContext?.role
    : undefined;

  const userId = user?.id;

  useEffect(() => {
    if (userId && !rumContextUpdated) {
      setRumContextUpdated(true);
      awsRumClient.addSessionAttributes({
        userId,
      });
    }
  }, [userId, rumContextUpdated]);

  if (user && !error) {
    const customsBroker = queryResponse?.searchCustomsBrokers?.results[0];
    const assumedUser = user.assumedUser;
    const currentUser = assumedUser ? assumedUser : user;
    const accountType = getUserAccountType(currentUser as any);
    // If this is ever true then we probably added a new user type and missed adding it in the map in getUserAccountType
    if (!accountType) {
      recordUIEvent(RumUIEvent.UNKNOWN_ACCOUNT_TYPE, {
        message: 'Unknown account type for the user',
        accountType: currentUser?.__typename,
        assumedUser: !!assumedUser,
      });
      return {};
    }
    const account = currentUser[accountType.toLowerCase()];
    return {
      ...currentUser,
      ...(assumedUser && { originalUser: user }),
      accountType,
      account,
      ...(role && { role }),
      ...(role === 'ADMIN' && { isPaamSecondaryUser: true }),
      ...(customsBroker &&
        Object.keys(customsBroker).length > 0 && { customsBroker }),
    };
  }
  if (loading) return { loading: true };

  // TODO temporary - decide how to handle
  // eslint-disable-next-line no-console
  if (error) console.error(error);
  return {};
};

export type CurrentUser = AnyObject & {
  id: string;
  accountType: AccountType;
  businessSupportTeams?: { nodes: { id: string; tags?: Tag[] }[] };
};
